import React from "react";
import "./theme.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import AuthProvider, { useAuth } from "./context/AuthContext";
import { RequireAuth, RequireRole } from "./routing/Protected";
import OrgProvider from "./context/OrgContext";
import { TerminologyProvider } from "./context/TerminologyContext";
import Layout from "./components/Layout";

import Login from "./pages/Login";
import ForcePasswordChange from "./pages/ForcePasswordChange";
import StatusPage from "./pages/StatusPage";
import AuditTrailPage from "./pages/AuditTrailPage";
import RetrieveCoaPage from "./pages/RetrieveCoaPage";
import ChartsPage from "./pages/ChartsPage";
import CoaExtractionsPage from "./pages/user/CoaExtractionsPage";
import ReviewCoasPage from "./pages/manager/ReviewCoasPage";
import ReviewVendorsMaterialsPage from "./pages/manager/ReviewVendorsMaterialsPage";
import UploadCoaPage from "./pages/vendor/UploadCoaPage";
import VendorMaterialsPage from "./pages/superadmin/VendorMaterialsPage";
import AddManageUsersPage from "./pages/superadmin/AddManageTeamsPage";
import ConfigureNotifyPage from "./pages/superadmin/ConfigureNotifyPage";
import FailedLotsPage from "./pages/superadmin/FailedLotsPage";
import NotificationsPage from "./pages/NotificationsPage";
import ForgotPassword from "./pages/ForgotPassword";
import KPIDashboard from "./pages/KPIDashboard";

import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminVendorMaterialsPage from "./pages/admin/VendorMaterialsPage";
import AdminConfigureNotifyPage from "./pages/admin/ConfigureNotifyPage";
import UsageLedgerPage from "./pages/UsageLedgerPage";

const NON_VENDOR_ROLES = ["User", "Viewer", "Manager", "Admin", "Super Admin"];
const ANALYTICAL_ROLES = ["User", "Viewer", "Manager"];

function AuthenticatedPage({ roles, children }) {
  const page = <Layout>{children}</Layout>;
  return (
    <RequireAuth>
      {roles ? <RequireRole roles={roles}>{page}</RequireRole> : page}
    </RequireAuth>
  );
}

function RoleAwareHome() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <StatusPage />;
}

export default function App() {
  return (
    <AuthProvider>
      <OrgProvider>
        <TerminologyProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route
                path="/force-password"
                element={
                  <RequireAuth>
                    <ForcePasswordChange />
                  </RequireAuth>
                }
              />
              <Route
                path="/"
                element={
                  <AuthenticatedPage>
                    <RoleAwareHome />
                  </AuthenticatedPage>
                }
              />
              <Route
                path="/status"
                element={
                  <AuthenticatedPage>
                    <StatusPage />
                  </AuthenticatedPage>
                }
              />
              <Route
                path="/audit"
                element={
                  <AuthenticatedPage roles={NON_VENDOR_ROLES}>
                    <AuditTrailPage />
                  </AuthenticatedPage>
                }
              />
              <Route
                path="/retrieve"
                element={
                  <AuthenticatedPage roles={ANALYTICAL_ROLES}>
                    <RetrieveCoaPage />
                  </AuthenticatedPage>
                }
              />
              <Route
                path="/charts"
                element={
                  <AuthenticatedPage roles={ANALYTICAL_ROLES}>
                    <ChartsPage />
                  </AuthenticatedPage>
                }
              />
              <Route
                path="/kpi"
                element={
                  <AuthenticatedPage roles={ANALYTICAL_ROLES}>
                    <KPIDashboard />
                  </AuthenticatedPage>
                }
              />
              <Route
                path="/user/coa"
                element={
                  <AuthenticatedPage roles={["User"]}>
                    <CoaExtractionsPage />
                  </AuthenticatedPage>
                }
              />
              <Route
                path="/manager/review-materials"
                element={
                  <AuthenticatedPage roles={["Manager"]}>
                    <ReviewVendorsMaterialsPage />
                  </AuthenticatedPage>
                }
              />
              <Route
                path="/manager/review-coas"
                element={
                  <AuthenticatedPage roles={["Manager"]}>
                    <ReviewCoasPage />
                  </AuthenticatedPage>
                }
              />
              <Route
                path="/vendor/upload"
                element={
                  <AuthenticatedPage roles={["Vendor"]}>
                    <UploadCoaPage />
                  </AuthenticatedPage>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AuthenticatedPage roles={["Admin"]}>
                    <AdminUsersPage />
                  </AuthenticatedPage>
                }
              />
              <Route
                path="/admin/vendors"
                element={
                  <AuthenticatedPage roles={["Admin"]}>
                    <AdminVendorMaterialsPage />
                  </AuthenticatedPage>
                }
              />
              <Route
                path="/admin/notify"
                element={
                  <AuthenticatedPage roles={["Admin"]}>
                    <AdminConfigureNotifyPage />
                  </AuthenticatedPage>
                }
              />
              <Route
                path="/superadmin/vendors"
                element={
                  <AuthenticatedPage roles={["Super Admin"]}>
                    <VendorMaterialsPage />
                  </AuthenticatedPage>
                }
              />
              <Route
                path="/superadmin/users"
                element={
                  <AuthenticatedPage roles={["Super Admin"]}>
                    <AddManageUsersPage />
                  </AuthenticatedPage>
                }
              />
              <Route
                path="/notifications"
                element={
                  <AuthenticatedPage>
                    <NotificationsPage />
                  </AuthenticatedPage>
                }
              />
              <Route
                path="/superadmin/notify"
                element={
                  <AuthenticatedPage roles={["Super Admin"]}>
                    <ConfigureNotifyPage />
                  </AuthenticatedPage>
                }
              />
              <Route
                path="/superadmin/failed-lots"
                element={
                  <AuthenticatedPage roles={["Super Admin"]}>
                    <FailedLotsPage />
                  </AuthenticatedPage>
                }
              />
              <Route
                path="/usage"
                element={
                  <AuthenticatedPage roles={NON_VENDOR_ROLES}>
                    <UsageLedgerPage />
                  </AuthenticatedPage>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </TerminologyProvider>
      </OrgProvider>
    </AuthProvider>
  );
}
